import {
  Checkbox,
  Grid,
  Icon,
  Input,
  Popup,
  Ref,
  Select,
} from "semantic-ui-react";
import { useNetworkFormContext } from "./context/NetworkFormContext";
import { useSelector } from "react-redux";
import { getDomains, getProjectsObject } from "../../../../selectors/projects";
import {
  getFullRegionName,
  renderZonesForSelectBox,
} from "../../../../app_shared_functions";
import FancyHeader from "../../../../components/shared/FancyHeader";
import { formFieldWarningClassName } from "../../../../shared-functions/form";
import { safeToLowerCase } from "../../../../shared-functions/string";
import Expandable from "../../../../components/shared/Expandable";
import MTUStatus from "./bits/MTUStatus";
import useZoneId from "../../../../custom-hooks/region/useZoneId";
import Subnets from "./subnets/SubnetsList";
import SubnetsDivider from "./subnets/bits/Divider";
import RouterName from "./router/RouterName";
import ExternalConnection from "./router/ExternalConnection";
import useCloseSlidingPanel from "../../../../components/slidingpanel/useCloseSlidingPanel";
import useNetworkCreationHandler from "./hooks/useCreateNetworkAction";
import useCreateClickErrorHandler from "./hooks/useCreateClickErrorHandler";
import useSubnetsValidation from "./subnets/hooks/useSubnetsValidation";

type Props = {
  createAnother: boolean;
  changeCreateAnother: () => void;
};

const NetworkCreateContent = ({
  createAnother,
  changeCreateAnother,
}: Props) => {
  const {
    formData,
    error,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    nameRef,
    regionRef,
    formWarning,
    showFormWarning,
    predefined_params,
  } = useNetworkFormContext();

  const domains = useSelector(getDomains);
  const projects = useSelector(getProjectsObject);
  const areasList = renderZonesForSelectBox(projects, domains);

  const zoneId = useZoneId(formData?.region || predefined_params?.region);
  const onCloseSlidingPanel = useCloseSlidingPanel();

  const { prepareNetworkCreation, isCreating } =
    useNetworkCreationHandler(createAnother);

  const subnetsError = useSubnetsValidation();

  const onDisabledCreateClick = useCreateClickErrorHandler({
    error,
    subnetsError,
    showFormWarning,
  });

  return (
    <div className="creator-component-wrapper">
      <FancyHeader title="Create Network" knowledgeBase />
      <p />

      <Grid>
        {/* NAME - REGION */}
        <Grid.Row className="separator padding-top-30">
          <Grid.Column textAlign="left" width={8} className="flex vcenter ">
            <h5>Name</h5>
          </Grid.Column>
          <Grid.Column textAlign="left" width={8}>
            <Ref innerRef={nameRef}>
              <Input
                value={formData.name}
                name="name"
                className={`select-box full ${formFieldWarningClassName(
                  formWarning,
                  error?.ref,
                  nameRef,
                )}`}
                onChange={handleInputChange}
              />
            </Ref>
          </Grid.Column>
          <Grid.Column
            textAlign="left"
            width={8}
            className="flex vcenter margin-top-30"
          >
            <h5>Region</h5>
          </Grid.Column>
          <Grid.Column
            textAlign="left"
            width={8}
            className="flex vcenter margin-top-30"
          >
            <Ref innerRef={regionRef}>
              <Select
                disabled={Boolean(predefined_params?.region)}
                icon="chevron circle down"
                className={`select-box full ${formFieldWarningClassName(
                  formWarning,
                  error?.ref,
                  regionRef,
                )}`}
                placeholder={
                  predefined_params?.zone
                    ? getFullRegionName(
                        domains,
                        safeToLowerCase(predefined_params.region),
                      )
                    : "Choose Region"
                }
                options={areasList}
                name="region"
                onChange={handleSelectChange}
              />
            </Ref>
          </Grid.Column>
        </Grid.Row>

        {/* ADVANCED */}
        {formData.region || predefined_params?.region ? (
          <>
            <Expandable defaultOpened={false} title="Advanced Options">
              <Grid.Row>
                <MTUStatus
                  zoneId={zoneId}
                  value={formData.mtu}
                  onChange={handleInputChange}
                />
                <Grid.Column
                  textAlign="left"
                  width={8}
                  className="margin-top-20 flex vbottom"
                >
                  <h5>Port Security</h5>
                </Grid.Column>

                <Grid.Column
                  textAlign="left"
                  width={8}
                  className="margin-top-20"
                >
                  <Checkbox
                    className="display-inlineblock"
                    name="portSecurity"
                    toggle
                    checked={formData.portSecurity}
                    label={formData.portSecurity ? "Enabled" : "Disabled"}
                    onChange={handleCheckboxChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Expandable>
            <Grid.Row className="separator padding-top-00"></Grid.Row>
          </>
        ) : null}

        {/* COMPLETE NETWORK  */}
        <Grid.Row className="padding-top-00">
          <Grid.Column textAlign="left" width={16} className="">
            <Popup
              trigger={
                <Checkbox
                  disabled={!formData.region && !predefined_params?.region}
                  className="simple-checkbox"
                  label="Create a complete network, containing a subnet and a router"
                  name="completeCreate"
                  checked={formData.completeCreate}
                  onChange={handleCheckboxChange}
                />
              }
              content={
                formData.region || predefined_params?.region ? (
                  <p>
                    By activating this option, you can setup a list of subnets
                    and a router to be created and attached to your network
                    automatically.
                  </p>
                ) : (
                  <p>Please choose a region first.</p>
                )
              }
            />
          </Grid.Column>
        </Grid.Row>

        {/* COMPLETE PARTS  */}
        {formData.completeCreate ? (
          <>
            <Subnets />

            <SubnetsDivider />

            <RouterName
              onChange={handleInputChange}
              placeholder={formData.name ? `${formData.name}-router` : ""}
              value={formData.routerName}
            />
            <ExternalConnection
              onChange={handleSelectChange}
              externalNetworkId={formData.externalNetworkId}
            />
          </>
        ) : null}

        <Grid.Row className="separator padding-top-00 padding-bottom-00"></Grid.Row>

        <Grid.Row className=" padding-top-00">
          <Grid.Column textAlign="left" width={16}>
            {error || subnetsError ? (
              <Popup
                trigger={
                  <button
                    className="float-right button button--green button--disabled button--icon__left"
                    onClick={onDisabledCreateClick}
                  >
                    <Icon name="exclamation circle" />
                    <span>Create</span>
                  </button>
                }
              >
                {error?.message || subnetsError?.message}
              </Popup>
            ) : isCreating ? (
              <button className="float-right button button--green overflow-hidden button--icon__right">
                <Icon loading name="spinner" />
                <span>Creating</span>
              </button>
            ) : (
              <button
                className="float-right button button--green"
                onClick={prepareNetworkCreation}
              >
                <span>Create</span>
              </button>
            )}
            <Checkbox
              className="simple-checkbox float-right margin-top-half  "
              label="Create Another"
              checked={createAnother}
              onChange={changeCreateAnother}
            />
            <button
              className="button button--bordered"
              onClick={onCloseSlidingPanel}
            >
              <span>Back</span>
            </button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default NetworkCreateContent;
